/**
 * Agent-builder feature constants.
 *
 * The feature uses the workspace-scoped Rain hooks
 * (`@/generated/hooks/rain/v1/workspace_scoped/*`), whose URLs are nested under
 * `/organizations/{org}/workspaces/{workspace}` — so calls take `(organization,
 * workspace, …)` directly and need no `parent` param.
 */

import taxonomy from './agent-role-taxonomy.json';
import templateIndex from './agent-templates/index.json';

/**
 * Tone enum values the backend validates for the `tone_style` memory block.
 * A template's `tone` must be one of these — anything else is rejected on write.
 * Kept in sync with the dropdown in `pages/agents/edit/config/IdentitySection`.
 */
export const TEMPLATE_TONE_VALUES = [
  'objective',
  'empathetic',
  'formal',
  'casual',
  'assertive',
  'diplomatic',
] as const;

/** Style enum values the backend validates for the `tone_style` block. */
export const TEMPLATE_STYLE_VALUES = [
  'telegraphic',
  'didactic',
  'exploratory',
  'synthetical',
  'verbose',
] as const;

/**
 * How much human oversight a template's workflow needs.
 *
 * Retained as authored metadata (and validated, so a generated template can't
 * carry a bogus value) but no longer surfaced in the gallery — as a filter it
 * asked users to pick an oversight level before they knew what the agent did.
 */
export const TEMPLATE_AUTONOMY_VALUES = [
  'assisted',
  'supervised',
  'autonomous',
] as const;

export type TTemplateTone = (typeof TEMPLATE_TONE_VALUES)[number];
export type TTemplateStyle = (typeof TEMPLATE_STYLE_VALUES)[number];
export type TTemplateAutonomy = (typeof TEMPLATE_AUTONOMY_VALUES)[number];

/** A team of roles inside a domain, e.g. Controllership or Lending. */
export interface IRoleGroup {
  /** Stable key, unique across every domain. Cards reference it as `group`. */
  key: string;
}

/** A top-level domain, e.g. Finance & Accounting or Banking. */
export interface IRoleDomain {
  /** Stable key. Cards reference it as `domain`; it also names the identity chunk. */
  key: string;
  groups: IRoleGroup[];
}

/**
 * The gallery-facing half of a template: everything needed to render a card,
 * filter it, and name the agent it creates.
 *
 * Each template is a job role (AP Specialist, Close Manager, Loan Officer)
 * rather than a single task. A role runs several automations from the
 * automations catalog, listed in `automations`.
 *
 * This is what ships in `agent-templates/index.json` and loads with the page.
 * The heavy `instructions` + `persona` prose lives in separate per-domain
 * chunks fetched on click — see `template-identity.ts` for why.
 */
export interface ITemplateCard {
  /**
   * Stable identifier. Used for the card's `data-testid` and as the React key,
   * so it must be unique across the library and must not change once shipped.
   * Also the key into the identity chunk.
   */
  id: string;
  /** The role's title: the card heading and the base for the new agent's name. */
  title: string;
  /** One-sentence card blurb (≤120 chars). Not written to the agent. */
  summary: string;
  /** Domain key, e.g. `finance`. Must exist in the taxonomy. */
  domain: string;
  /** Group key, e.g. `controllership`. Must belong to `domain`. */
  group: string;
  /** Other titles the role goes by, so a search for "AP clerk" finds it. */
  alsoKnownAs: string[];
  /**
   * Automations-catalog activity ids this role runs
   * (`src/modules/process-catalog/catalog.json`). Every catalog activity is
   * owned by exactly one role.
   */
  automations: string[];
  /** What the role won't do, as a verb phrase ("Release payments."). */
  wontDo: string;
  /** Ids of related roles it hands work to or from, shown as "works with". */
  worksWith: string[];
  /** Lowercase search terms, including acronyms and synonyms. */
  keywords: string[];
  /** How much human oversight the workflow needs. Authored, not displayed. */
  autonomy: TTemplateAutonomy;
  /** 1–3 sentences written to the agent's Goal block. */
  description: string;
  /** Tone enum for the `tone_style` block. */
  tone: TTemplateTone;
  /** Style enum for the `tone_style` block. */
  style: TTemplateStyle;
}

/**
 * A complete prebuilt agent template — a card plus its identity prose.
 *
 * A template is a fully-formed starting configuration, not just a prompt: these
 * fields map directly onto memory blocks that `seedTemplateConfig` writes to the
 * new agent's config thread before the Builder chat opens.
 *
 * Field → memory block:
 *   - `description`  → `text` block named "description" (the agent's Goal)
 *   - `instructions` → `system_instructions` block
 *   - `persona`      → `persona` block
 *   - `tone`/`style` → `tone_style` block (`config.tone` + `config.style`)
 *
 * The domain, group, automations, summary, and keywords are gallery metadata,
 * never written. The instructions name the role's automations themselves.
 *
 * Obtained from a card via `loadAgentTemplate`, which fetches the identity chunk.
 */
export interface IAgentTemplate extends ITemplateCard {
  /** Markdown written to the `system_instructions` block. */
  instructions: string;
  /** Markdown written to the `persona` block. */
  persona: string;
}

/**
 * The job-role taxonomy the gallery is organized by: domain → group → role.
 * Keys are stable and referenced by every template, so extend this rather than
 * renaming them. Order here is gallery order.
 */
export const AGENT_ROLE_TAXONOMY = taxonomy.domains as IRoleDomain[];

/**
 * The prebuilt role library as gallery cards, from `agent-templates/index.json`,
 * in taxonomy order.
 *
 * Card fields only — `instructions` and `persona` are fetched per domain on
 * click (`loadAgentTemplate`), which keeps the prompt markdown out of the
 * create page's initial payload.
 *
 * The data lives in JSON (not TS) so the library can be generated and edited in
 * bulk without touching code. Nothing here can verify that cast, so
 * `constants.test.ts` owns the check: it asserts every record satisfies the
 * interface, that the tone/style enums are backend-valid, that the domain and
 * group keys resolve and nest, and that every automation resolves and has
 * exactly one owner.
 */
export const AGENT_TEMPLATES = templateIndex as ITemplateCard[];

/** Flat `domain` key → domain lookup. */
export const ROLE_DOMAIN_BY_KEY: Record<string, IRoleDomain> = {};
/** Flat `group` key → group lookup. */
export const ROLE_GROUP_BY_KEY: Record<string, IRoleGroup> = {};
/** `group` key → owning `domain` key, so a group selection can highlight its domain. */
export const ROLE_DOMAIN_OF_GROUP: Record<string, string> = {};

for (const domain of AGENT_ROLE_TAXONOMY) {
  ROLE_DOMAIN_BY_KEY[domain.key] = domain;
  for (const group of domain.groups) {
    ROLE_GROUP_BY_KEY[group.key] = group;
    ROLE_DOMAIN_OF_GROUP[group.key] = domain.key;
  }
}

/** How many templates sit under each domain. Drives the nav's counts. */
export function countTemplatesByDomain(
  list: readonly ITemplateCard[] = AGENT_TEMPLATES,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const template of list) {
    counts[template.domain] = (counts[template.domain] ?? 0) + 1;
  }
  return counts;
}

/** How many templates sit under each group. Drives the nav's counts. */
export function countTemplatesByGroup(
  list: readonly ITemplateCard[] = AGENT_TEMPLATES,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const template of list) {
    counts[template.group] = (counts[template.group] ?? 0) + 1;
  }
  return counts;
}

/**
 * The bdk tool-source endpoint the book (bdk) connection is served from. Passed
 * as `config.url` when creating a `bdk` tool source (POST /tool-sources) so the
 * backend resolves the book library from this in-cluster address.
 */
export const BDK_TOOL_SOURCE_URL = 'k8s://bdk-library.bdk:9090';

/**
 * The automations (Grimoire) tool-source endpoint. Passed as `config.url` when
 * creating an `automations` tool source (POST /tool-sources) so the backend
 * resolves the workspace's published automations from this in-cluster address.
 */
export const AUTOMATIONS_TOOL_SOURCE_URL = 'k8s://grimoire.grimoire:9090';

/**
 * Marks a thread as the builder's internal config/test thread (the one
 * `resolveConfigThread` reuses for the Build tab and the Playground sandbox),
 * not a real conversation with the deployed agent. Written to the thread's
 * `description` at create time.
 *
 * `variables` looks like the natural home, but rain validates it as a *closed
 * set* against the agent template's declared variable definitions and rejects
 * every other key with `TEMPLATE_VARIABLE_REF_UNKNOWN` — so marking a thread
 * that way didn't hide it, it made `CreateThread` fail outright with a 400.
 * `description` is settable on `CreateThreadRequest` and filterable by
 * `ListThreads`, which is all a marker needs. (`metadata` is filterable too,
 * and `V1Thread` reads it, but no request message exposes it — rain writes it
 * server-side only.)
 *
 * Setting a description has a second, wanted effect: rain's auto-title pass
 * skips any thread that already has a title or description, so the builder's
 * sandbox thread no longer burns an LLM call being named.
 */
export const BUILDER_CONFIG_THREAD_MARKER = '__kognitos_builder_config';
